import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { StorageAdapter } from '@/lib/storage-adapter';

const STORAGE_KEY = 'bf_timer_state';

interface TimerState {
  isActive: boolean;
  isPaused: boolean;
  activeSide: 'left' | 'right';
  leftSeconds: number;
  rightSeconds: number;
  segmentStartedAt: number | null; // Date.now() when current segment started
  babyId: string | null;
}

const DEFAULT_STATE: TimerState = {
  isActive: false,
  isPaused: false,
  activeSide: 'left',
  leftSeconds: 0,
  rightSeconds: 0,
  segmentStartedAt: null,
  babyId: null,
};

interface BreastfeedTimerContextType {
  timerState: TimerState;
  liveLeft: number;
  liveRight: number;
  liveTotal: number;
  start: (side: 'left' | 'right', babyId: string) => void;
  switchSide: () => void;
  pause: () => void;
  resume: () => void;
  discard: () => void;
  getSessionData: () => { leftSec: number; rightSec: number; totalSec: number; startedAt: Date };
}

const BreastfeedTimerContext = createContext<BreastfeedTimerContextType | null>(null);

function loadState(): TimerState {
  try {
    const raw = StorageAdapter.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_STATE, ...parsed };
    }
  } catch {
    // ignore parse errors
  }
  return DEFAULT_STATE;
}

function saveState(state: TimerState): void {
  try {
    StorageAdapter.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

export function BreastfeedTimerProvider({ children }: { children: ReactNode }) {
  const [timerState, setTimerState] = useState<TimerState>(loadState);
  const [tick, setTick] = useState(0);

  // Persist on every state change
  useEffect(() => {
    saveState(timerState);
  }, [timerState]);

  // Tick interval when active and not paused
  useEffect(() => {
    if (timerState.isActive && !timerState.isPaused && timerState.segmentStartedAt !== null) {
      const interval = setInterval(() => {
        setTick(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timerState.isActive, timerState.isPaused, timerState.segmentStartedAt]);

  // Compute elapsed seconds for the current segment
  const segmentElapsed =
    timerState.isActive && !timerState.isPaused && timerState.segmentStartedAt !== null
      ? Math.floor((Date.now() - timerState.segmentStartedAt) / 1000)
      : 0;

  const liveLeft =
    timerState.activeSide === 'left'
      ? timerState.leftSeconds + segmentElapsed
      : timerState.leftSeconds;
  const liveRight =
    timerState.activeSide === 'right'
      ? timerState.rightSeconds + segmentElapsed
      : timerState.rightSeconds;
  const liveTotal = liveLeft + liveRight;

  // Suppress unused tick warning — it exists solely to trigger re-renders
  void tick;

  const start = useCallback((side: 'left' | 'right', babyId: string) => {
    const newState: TimerState = {
      isActive: true,
      isPaused: false,
      activeSide: side,
      leftSeconds: 0,
      rightSeconds: 0,
      segmentStartedAt: Date.now(),
      babyId,
    };
    setTimerState(newState);
  }, []);

  const switchSide = useCallback(() => {
    setTimerState(prev => {
      const elapsed =
        prev.segmentStartedAt !== null && !prev.isPaused
          ? Math.floor((Date.now() - prev.segmentStartedAt) / 1000)
          : 0;
      const newSide = prev.activeSide === 'left' ? 'right' : 'left';
      return {
        ...prev,
        activeSide: newSide,
        leftSeconds: prev.activeSide === 'left' ? prev.leftSeconds + elapsed : prev.leftSeconds,
        rightSeconds: prev.activeSide === 'right' ? prev.rightSeconds + elapsed : prev.rightSeconds,
        segmentStartedAt: Date.now(),
        isPaused: false,
      };
    });
  }, []);

  const pause = useCallback(() => {
    setTimerState(prev => {
      const elapsed =
        prev.segmentStartedAt !== null && !prev.isPaused
          ? Math.floor((Date.now() - prev.segmentStartedAt) / 1000)
          : 0;
      return {
        ...prev,
        leftSeconds: prev.activeSide === 'left' ? prev.leftSeconds + elapsed : prev.leftSeconds,
        rightSeconds: prev.activeSide === 'right' ? prev.rightSeconds + elapsed : prev.rightSeconds,
        isPaused: true,
        segmentStartedAt: null,
      };
    });
  }, []);

  const resume = useCallback(() => {
    setTimerState(prev => ({
      ...prev,
      isPaused: false,
      segmentStartedAt: Date.now(),
    }));
  }, []);

  const discard = useCallback(() => {
    setTimerState(DEFAULT_STATE);
    saveState(DEFAULT_STATE);
  }, []);

  const getSessionData = useCallback(() => {
    return {
      leftSec: liveLeft,
      rightSec: liveRight,
      totalSec: liveTotal,
      startedAt: new Date(Date.now() - liveTotal * 1000),
    };
  }, [liveLeft, liveRight, liveTotal]);

  return (
    <BreastfeedTimerContext.Provider
      value={{
        timerState,
        liveLeft,
        liveRight,
        liveTotal,
        start,
        switchSide,
        pause,
        resume,
        discard,
        getSessionData,
      }}
    >
      {children}
    </BreastfeedTimerContext.Provider>
  );
}

export function useBreastfeedTimer(): BreastfeedTimerContextType {
  const ctx = useContext(BreastfeedTimerContext);
  if (!ctx) throw new Error('useBreastfeedTimer must be used within BreastfeedTimerProvider');
  return ctx;
}
